// src/components/settings/shared/dialogs/UserDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  DailogLabel,
  DialogLabelText,
  StyledTextField,
} from '../../../forms/shared/FormElements';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import CustomSelect from '../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../common/CustomSelectMenuItem';
import PasswordField from '../../../common/PasswordField';
import InfoTooltip from '../../../common/InfoTooltip';
import { UserDto } from '../../../../services/api';

type UserRole = 'Admin' | 'Manager' | 'Staff';

interface UserFormData {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: UserFormData) => void;
  editing: UserDto | null;
  errorMessage?: string | null;
}

const UserDialog: React.FC<UserDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  errorMessage,
}) => {
  const [formData, setFormData] = useState<UserFormData>({
    username: '',
    email: '',
    password: '',
    role: 'Staff',
  });
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (open) {
      setConfirmPassword('');
      setFormData(
        editing
          ? {
              username: editing.username,
              email: editing.email,
              password: '',
              role: editing.role as UserRole,
            }
          : { username: '', email: '', password: '', role: 'Staff' }
      );
    }
  }, [open, editing]);

  const hasPassword = formData.password.length > 0;
  const passwordsMatch = formData.password === confirmPassword;

  const canSave =
    formData.username.trim().length > 0 &&
    formData.email.trim().length > 0 &&
    (editing ? !hasPassword || passwordsMatch : hasPassword && passwordsMatch);

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit User' : 'Add User'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      maxWidth="sm"
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text="Save"
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={0.5} mt={0.2}>
        <DailogLabel>
          <DialogLabelText id="user-username-label">Username:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="user-username-input"
          inputProps={{ 'aria-labelledby': 'user-username-label' }}
          value={formData.username}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, username: e.target.value })
          }
          required
          fullWidth
        />

        <DailogLabel>
          <DialogLabelText id="user-email-label">Email:</DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="user-email-input"
          inputProps={{ 'aria-labelledby': 'user-email-label' }}
          type="email"
          value={formData.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, email: e.target.value })
          }
          required
          fullWidth
          error={!!errorMessage}
          helperText={errorMessage || ''}
        />

        <DailogLabel>
          <DialogLabelText
            id="user-password-label"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            {editing ? 'New Password:' : 'Password:'}
            {editing && (
              <InfoTooltip text="Leave blank to keep current password" />
            )}
          </DialogLabelText>
        </DailogLabel>
        <PasswordField
          component={StyledTextField}
          id="user-password-input"
          inputProps={{ 'aria-labelledby': 'user-password-label' }}
          value={formData.password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, password: e.target.value })
          }
          required={!editing}
          fullWidth
        />

        {(!editing || hasPassword) && (
          <>
            <DailogLabel>
              <DialogLabelText id="user-confirm-password-label">
                Confirm Password:
              </DialogLabelText>
            </DailogLabel>
            <PasswordField
              component={StyledTextField}
              id="user-confirm-password-input"
              inputProps={{
                'aria-labelledby': 'user-confirm-password-label',
              }}
              value={confirmPassword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setConfirmPassword(e.target.value)
              }
              required
              fullWidth
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={
                confirmPassword.length > 0 && !passwordsMatch
                  ? 'Passwords do not match'
                  : ''
              }
            />
          </>
        )}

        <DailogLabel>
          <DialogLabelText>Role:</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={formData.role}
          onChange={(value) =>
            setFormData({ ...formData, role: value as UserRole })
          }
          ariaLabel="Role"
        >
          <CustomSelectMenuItem value="Staff">Staff</CustomSelectMenuItem>
          <CustomSelectMenuItem value="Manager">Manager</CustomSelectMenuItem>
          <CustomSelectMenuItem value="Admin">Admin</CustomSelectMenuItem>
        </CustomSelect>
      </Box>
    </DialogBase>
  );
};

export default UserDialog;
